import { useState, useEffect, useRef } from "react";
import supabase from "../../lib/supabase";
import { WorkspaceDeleteModal } from "./WorkspaceDeleteModal";

function WorkspaceMembersModal({ workspace, user, userRole, t, lang = "EN", onClose, onDeleted }: any) {
  const [members, setMembers] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [inviteRole, setInviteRole] = useState("member");
  const [fetchLoading, setFetchLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [notifyWarning, setNotifyWarning] = useState("");
  const overlayRef = useRef(null);
  const canManage = userRole === "owner" || userRole === "co-owner";
  // Role labels are shared by the invite dropdown and the success message, so a
  // translated invite result never mixes languages with the control that set it.
  const roleLabel = (r: string) => r === "co-owner"
    ? (t?.workspaceRoleCoOwner || "Co-Owner")
    : (t?.workspaceRoleMember || "Member");

  useEffect(() => {
    // The delete modal renders on top of this one and binds its own Escape
    // handler to document. Both would fire on the same keypress, cancelling the
    // deletion and dismissing this modal underneath it. Stand down while it is
    // open — stopPropagation cannot help when both listeners sit on document.
    if (confirmDelete) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose, confirmDelete]);

  useEffect(() => { loadMembers(); }, []);

  const loadMembers = async () => {
    setFetchLoading(true);
    const { data: mems } = await supabase.from("workspace_members").select("*").eq("workspace_id", workspace.id);
    if ((mems as any[])?.length) {
      const withEmails = await Promise.all((mems as any[]).map(async (m: any) => {
        const { data: p } = await supabase.from("profiles").select("email").eq("id", m.user_id).single();
        return { ...m, email: (p as any)?.email || "Unknown" };
      }));
      setMembers(withEmails);
    } else { setMembers([]); }
    setFetchLoading(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setLoading(true); setError(""); setSuccess("");
    const { data: lookup, error: lookupErr } = await supabase.rpc("lookup_profile_by_email", { p_email: inviteEmail.trim() });
    if (lookupErr) { setError(lookupErr.message); setLoading(false); return; }
    const p = Array.isArray(lookup) ? lookup[0] : lookup;
    if (!p?.id) { setError(t?.workspaceInviteNoAccount || "No CHASS1S account found with that email. Ask them to sign up first."); setLoading(false); return; }
    const { data: existing } = await supabase.from("workspace_members").select("id").eq("workspace_id", workspace.id).eq("user_id", p.id).single();
    if ((existing as any)?.id) { setError(t?.workspaceInviteAlreadyMember || "That user is already a member of this workspace."); setLoading(false); return; }
    const { error: err } = await supabase.from("workspace_members").insert({ workspace_id: workspace.id, user_id: p.id, role: inviteRole, invited_by: user.id });
    if (err) { setError(err.message); setLoading(false); return; }

    const invited = inviteEmail.trim();
    setSuccess((t?.workspaceInviteAdded || "{email} added as {role}.")
      .replace("{email}", invited)
      .replace("{role}", roleLabel(inviteRole)));
    setInviteEmail(""); setInviteOpen(false); loadMembers();

    // The member is already added at this point. A failed notification must not
    // read as a failed invite — surface it as a warning and leave the row alone.
    setNotifyWarning("");
    const { error: mailErr } = await supabase.functions.invoke("send-workspace-invite", {
      body: { workspaceId: workspace.id, invitedUserId: p.id, role: inviteRole, lang },
    });
    if (mailErr) setNotifyWarning(t?.workspaceInviteMailFailed || 'Member added, but the notification email could not be sent.');

    setLoading(false);
  };

  const handleRemove = async (memberId: any, memberUserId: any) => {
    if (memberUserId === user.id) return;
    await supabase.from("workspace_members").delete().eq("id", memberId);
    loadMembers();
  };

  return (
    <div ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1300,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", width: "100%", maxWidth: 480, maxHeight: "85vh",
        display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
        {/* Header */}
        <div style={{ background: "#000", padding: "20px 24px", flexShrink: 0, position: "relative" }}>
          <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, background: "none",
            border: "none", cursor: "pointer", color: "#555", fontSize: 18 }}>✕</button>
          {userRole === "owner" && (
            <button onClick={() => setConfirmDelete(true)}
              title={t?.workspaceDelete || 'Delete workspace'}
              style={{ position: "absolute", bottom: 20, right: 14, background: "none",
                border: "none", cursor: "pointer", color: "#555", padding: 2,
                display: "flex", alignItems: "center" }}
              onMouseEnter={e => { e.currentTarget.style.color = "#cc0000"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#555"; }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" /><path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          )}
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#555",
            letterSpacing: "0.2em", marginBottom: 6 }}>
            {t?.workspaceConfigEyebrow || 'WORKSPACE · CONFIGURATION'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", fontFamily: "'Georgia', serif" }}>{workspace.name}</div>
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10, color: "#555", marginTop: 3 }}>
            Your role: <span style={{ color: "#aaa" }}>{userRole?.toUpperCase()}</span>
          </div>
        </div>
        {/* Members list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {/* Member count label removed — count is evident from the list */}
          {fetchLoading ? (
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: "#aaa" }}>Loading...</div>
          ) : members.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 0", borderBottom: "1px solid #f0f0f0", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#000",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.email}</div>
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#aaa", marginTop: 2 }}>
                  {m.role?.toUpperCase()}{m.user_id === user.id ? " · YOU" : ""}
                </div>
              </div>
              {canManage && m.user_id !== user.id && m.role !== "owner" && (
                <button onClick={() => handleRemove(m.id, m.user_id)}
                  style={{ flexShrink: 0, background: "none", border: "1px solid #e0e0e0",
                    padding: "4px 10px", cursor: "pointer", fontFamily: "'Courier New', monospace",
                    fontSize: 9, color: "#888", fontWeight: 700, letterSpacing: "0.06em" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#cc0000"; e.currentTarget.style.color = "#cc0000"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.color = "#888"; }}>
                  REMOVE
                </button>
              )}
            </div>
          ))}
        </div>
        {/* Invite section */}
        {canManage && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid #e0e0e0", flexShrink: 0 }}>
            {!inviteOpen && (
              <button onClick={() => setInviteOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6,
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                  color: "#888", fontFamily: "'Courier New', monospace",
                  fontWeight: 700, fontSize: 10, letterSpacing: "0.08em" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#000"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#888"; }}>
                <span style={{ fontSize: 14, lineHeight: 1, fontWeight: 400 }}>+</span>
                <span>{t?.workspaceAddMember || 'ADD MEMBER'}</span>
              </button>
            )}
            {/* Outside the collapsible: the form closes on success, and these
                must survive that collapse to be read at all. */}
            {success && <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10,
              color: "#006633", marginTop: inviteOpen ? 0 : 10, marginBottom: 8 }}>✓ {success}</div>}
            {notifyWarning && <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10,
              color: "#b26a00", marginBottom: 8, lineHeight: 1.5 }}>⚠ {notifyWarning}</div>}
            {inviteOpen && (
            <>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#aaa",
              letterSpacing: "0.12em", marginBottom: 10 }}>INVITE MEMBER</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                placeholder="email@example.com" onKeyDown={e => { if (e.key === "Enter") handleInvite(); }}
                style={{ flex: 1, padding: "9px 12px", fontFamily: "'Courier New', monospace", fontSize: 12,
                  border: "1px solid #d0d0d0", outline: "none", borderRadius: 2 }} />
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                style={{ padding: "9px 10px", fontFamily: "'Courier New', monospace", fontSize: 11,
                  border: "1px solid #d0d0d0", outline: "none", cursor: "pointer", borderRadius: 2 }}>
                <option value="member">{roleLabel("member")}</option>
                <option value="co-owner">{roleLabel("co-owner")}</option>
              </select>
            </div>
            {error && <div style={{ fontFamily: "'Courier New', monospace", fontSize: 10,
              color: "#cc0000", marginBottom: 8, lineHeight: 1.5 }}>{error}</div>}
            <button onClick={handleInvite} disabled={loading || !inviteEmail.trim()}
              style={{ width: "100%", padding: "10px", border: "none",
                background: inviteEmail.trim() ? "#000" : "#e8e8e8",
                color: inviteEmail.trim() ? "#fff" : "#aaa", cursor: inviteEmail.trim() ? "pointer" : "not-allowed",
                fontFamily: "'Courier New', monospace", fontSize: 11, fontWeight: 900, letterSpacing: "0.12em",
                borderRadius: 2 }}>
              {loading ? "ADDING..." : "ADD MEMBER"}
            </button>
            </>
            )}
          </div>
        )}
      </div>
      {confirmDelete && (
        <WorkspaceDeleteModal
          workspace={workspace} t={t}
          onCancel={() => setConfirmDelete(false)}
          onDeleted={() => { setConfirmDelete(false); onDeleted?.(workspace.id); }}
        />
      )}
    </div>
  );
}

export { WorkspaceMembersModal };
